import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import {
  TIMEKEEPING_NETWORK_RETRY_BASE_DELAY_MS,
  TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS,
  TIMEKEEPING_NETWORK_RETRY_MAX_DELAY_MS,
} from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/constants";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  type NfcHashValidationAction,
  NfcHashValidationResponseErrorType,
  postNfcHashValidationErrorResponseSchema,
  postNfcHashValidationRequestSchema,
  postNfcHashValidationResponseSchema,
} from "./types";

export interface UseNfcHashValidationProps {
  shiftId: string;
  hashString: string;
  action: NfcHashValidationAction;
}

export enum NfcHashValidationResult {
  SUCCESS = "SUCCESS",
  INVALID_HASH = "INVALID_HASH",
  INCORRECT_HASH = "INCORRECT_HASH",
  INACTIVE_HASH = "INACTIVE_HASH",
  OTHER_FAILURE = "OTHER_FAILURE",
  NETWORK_ERROR = "NETWORK_ERROR",
}

const mappingNfcHashValidationResponseErrorToResult: Record<
  NfcHashValidationResponseErrorType,
  NfcHashValidationResult
> = {
  [NfcHashValidationResponseErrorType.INVALID_HASH]: NfcHashValidationResult.INVALID_HASH,
  [NfcHashValidationResponseErrorType.INCORRECT_HASH]: NfcHashValidationResult.INCORRECT_HASH,
  [NfcHashValidationResponseErrorType.INACTIVE_HASH]: NfcHashValidationResult.INACTIVE_HASH,
  [NfcHashValidationResponseErrorType.OTHER_FAILURE]: NfcHashValidationResult.OTHER_FAILURE,
};

function convertNfcHashValidationErrorToResult(error: string): NfcHashValidationResult | undefined {
  if (error in mappingNfcHashValidationResponseErrorToResult) {
    return mappingNfcHashValidationResponseErrorToResult[
      error as NfcHashValidationResponseErrorType
    ];
  }

  return undefined;
}

async function attemptNfcHashValidationRequest(
  props: UseNfcHashValidationProps
): Promise<NfcHashValidationResult> {
  const { shiftId, hashString, action } = props;

  try {
    const response = await post({
      url: `${environmentConfig.REACT_APP_BASE_API_URL}/nfc-hash-validations`,
      data: {
        data: {
          type: "nfc-hash-validation",
          attributes: {
            action,
            hash: hashString,
          },
          relationships: {
            shift: { data: { type: "shift", id: shiftId } },
          },
        },
      },
      requestSchema: postNfcHashValidationRequestSchema,
      responseSchema: postNfcHashValidationResponseSchema,
    });

    const validationId = response.data?.data?.id;

    if (!validationId) {
      logError(APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_ERROR, {
        error: "Unknown NFC validation error: missing validation ID",
        metadata: {
          shiftId,
          hashString,
          action,
          response,
          validationId,
        },
      });

      return NfcHashValidationResult.OTHER_FAILURE;
    }

    return NfcHashValidationResult.SUCCESS;
  } catch (error: unknown) {
    if (!(error instanceof AxiosError)) {
      logError(APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_ERROR, {
        error: "Unknown NFC validation error: unknown error",
        metadata: { shiftId, hashString, action, error },
      });
      return NfcHashValidationResult.OTHER_FAILURE;
    }

    if (!error.response) {
      logError(APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_ERROR, {
        error: "Unknown NFC validation error: network error",
        metadata: { shiftId, hashString, action, error },
      });
      return NfcHashValidationResult.NETWORK_ERROR;
    }

    const validationResult = postNfcHashValidationErrorResponseSchema.safeParse(
      error.response?.data as unknown
    );
    if (!validationResult.success) {
      logError(APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_ERROR, {
        error: "Unknown NFC validation error: invalid JSON API error",
        metadata: {
          shiftId,
          hashString,
          action,
          errorResponse: error.response,
          errorResponseData: error.response?.data,
          validationError: validationResult.error,
        },
      });
      return NfcHashValidationResult.OTHER_FAILURE;
    }

    const errorResponse = validationResult.data;
    const matchingKnownErrorType = convertNfcHashValidationErrorToResult(
      errorResponse.errors[0].code
    );
    if (matchingKnownErrorType) {
      return matchingKnownErrorType;
    }

    logError(APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_ERROR, {
      error: "Unknown NFC validation error: unknown error type",
      metadata: {
        shiftId,
        hashString,
        action,
        errorResponse: error.response,
        errorResponseData: error.response?.data,
        responseErrorCode: errorResponse.errors[0].code,
        matchingKnownErrorType,
      },
    });

    return NfcHashValidationResult.OTHER_FAILURE;
  }
}

async function sendNfcHashValidationRequest(
  props: UseNfcHashValidationProps
): Promise<NfcHashValidationResult> {
  for (let attempt = 0; attempt < TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS; attempt++) {
    const result = await attemptNfcHashValidationRequest(props);
    if (result !== NfcHashValidationResult.NETWORK_ERROR) {
      return result;
    }
    logEvent(APP_V2_APP_EVENTS.TIMEKEEPING_NETWORK_RETRY_ATTEMPT, {
      source: "nfc-hash-validation",
      attempt: attempt + 1,
      maxAttempts: TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS,
      shiftId: props.shiftId,
      action: props.action,
    });
    await new Promise<void>((resolve) =>
      setTimeout(
        resolve,
        Math.min(
          TIMEKEEPING_NETWORK_RETRY_BASE_DELAY_MS * 2 ** attempt,
          TIMEKEEPING_NETWORK_RETRY_MAX_DELAY_MS
        )
      )
    );
  }
  // Final attempt — return whatever result we get (could be NETWORK_ERROR)
  return attemptNfcHashValidationRequest(props);
}

export function useNfcHashValidation(): UseMutationResult<
  NfcHashValidationResult,
  unknown,
  UseNfcHashValidationProps
> {
  return useMutation({
    mutationFn: sendNfcHashValidationRequest,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_REQUEST_FAILED,
      logSuccessMessage: APP_V2_APP_EVENTS.NFC_HASH_VALIDATION_REQUEST_SUCCEEDED,
    },
  });
}
